import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteDocumentWorkflowTemplate } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useMasterDocumentsEndpoints } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsEndpoints.ts';

export const useDeleteMasterDocument = (onDeleteSuccess?: () => void) => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const queryClient = useQueryClient();
	const { list: listKey, byId } = useMasterDocumentsEndpoints();

	return useMutation({
		mutationFn: async ({ templateId }: { templateId: string }) => {
			if (!templateId) {
				throw new Error('Document template ID not found');
			}
			return await deleteDocumentWorkflowTemplate(byId(templateId));
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [listKey] });
			onDeleteSuccess?.();
			showToastV2({
				title: tMipManage('master_documents.toast.deleted'),
			});
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			const backendMessage = (error as { error?: { message?: string } })?.error?.message;
			if (typeof backendMessage === 'string' && backendMessage.length > 0) {
				showToastV2({
					state: 'error',
					title: backendMessage,
				});
				return;
			}
			showToastV2({
				state: 'error',
				title: tDefault('common.toast.error.message'),
			});
		},
	});
};
